import styled from '@emotion/styled';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Logout from '@mui/icons-material/Logout';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { googleLogout } from '@react-oauth/google';
import React from 'react';

import { getDesignTokens } from '~/client/components/base/core';
import Text from '~/client/components/base/text';
import { useUserProfile } from '~/client/hooks/user-profile';
import * as GQL from '~/graphql/generated/client';
import useLeftNavContext from '~c/components/base/left-nav/left-nav-context';
import Spacer from '~c/components/base/spacer';
import { settingsRoutesUtil } from '~c/routes';
import { nodeAuthApi } from '~c/services';

interface Props {
  className?: string;
}

const UserNavigationMenuImpl: React.FC<Props> = ({ className }) => {
  const [signOut] = nodeAuthApi.useLogoutMutation();
  const theme = useTheme();
  const tokens = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light'
  );
  const { userProfile } = useUserProfile();
  const { state } = useLeftNavContext();
  const { navColumnOpen } = state;

  const { data } = GQL.useUserOrganizationContextsQuery({
    skip: !userProfile.emailVerified,
    fetchPolicy: 'cache-and-network',
  });

  const currentOrgName = data?.userOrganizationContexts?.find(
    (ctx: any) => ctx?.organization?._id === userProfile.defaultOrganizationId
  )?.organization?.name;

  const initials =
    userProfile.firstName || userProfile.lastName
      ? `${(userProfile.firstName ?? '')[0] ?? ''}${(userProfile.lastName ?? '')[0] ?? ''}`.toUpperCase()
      : null;

  const { navigateTo: navigateToSettings } = settingsRoutesUtil.useHelper();

  const handleLogout = () => {
    signOut();
    googleLogout();
  };

  const avatarEl = (
    <Avatar
      sx={{
        width: 36,
        height: 36,
        bgcolor: tokens.avatarBg,
        color: tokens.avatarText,
        fontSize: 13,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials ?? <AccountCircle />}
    </Avatar>
  );

  return (
    <div className={className}>
      <Spacer className="grow" />
      <Box
        sx={{
          px: navColumnOpen ? 1.5 : 1,
          py: 1,
          borderTop: `1px solid ${tokens.nav.sidebarDivider}`,
          display: 'flex',
          alignItems: 'center',
          gap: navColumnOpen ? 1.5 : 0,
          justifyContent: navColumnOpen ? 'flex-start' : 'center',
          minWidth: 0,
        }}
      >
        {navColumnOpen ? (
          <>
            <ButtonBase
              onClick={() => navigateToSettings('PERSONAL')}
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textAlign: 'left',
                borderRadius: 1,
                p: 0.5,
                mx: -0.5,
                '&:hover': { opacity: 0.85 },
              }}
            >
              {avatarEl}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Text
                  sz="body14"
                  noWrap
                  sx={{ fontWeight: 600, color: tokens.nav.itemTextOnDark }}
                >
                  {userProfile.firstName} {userProfile.lastName}
                </Text>
                <Text
                  sz="body12"
                  noWrap
                  sx={{ color: tokens.nav.itemTextOnDark, opacity: 0.7 }}
                >
                  {userProfile.email}
                </Text>
                {currentOrgName && (
                  <Text
                    sz="body12"
                    noWrap
                    sx={{ color: tokens.nav.itemTextOnDark, opacity: 0.5 }}
                  >
                    {currentOrgName}
                  </Text>
                )}
              </Box>
            </ButtonBase>
            <Tooltip title="Sign out" arrow placement="top">
              <IconButton
                size="small"
                onClick={handleLogout}
                sx={{
                  color: tokens.nav.itemTextOnDark,
                  opacity: 0.6,
                  flexShrink: 0,
                  '&:hover': { opacity: 1 },
                }}
              >
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip
            title={`${userProfile.firstName} ${userProfile.lastName}`}
            arrow
            placement="right"
          >
            <Box
              onClick={() => navigateToSettings('PERSONAL')}
              sx={{ cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
            >
              {avatarEl}
            </Box>
          </Tooltip>
        )}
      </Box>
    </div>
  );
};

const UserNavigationMenu = styled(UserNavigationMenuImpl)`
  display: contents;

  .grow {
    flex-grow: 1;
  }
`;

export default UserNavigationMenu;
